import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GetModulesApiResponse,
  Module,
} from "../../../@types/DRO/GetModuleApiResponse";
import { GetModulesApi } from "../../../api/GetModules/GetModules";
import UserWarehouseApi from "../../../api/UserWarehouse/UserWarehouseApi";
import { useLoading } from "../../../views/Loader/LoadingContext";

const SideBarNavigation: React.FC = () => {
  const navigateTo = useNavigate();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState<Module[]>([]);
  const [expandedMenuItems, setExpandedMenuItems] = useState<
    Record<string, boolean>
  >({});
  const [warehouses, setWarehouses] = useState<Array<any>>([]);
  const [isWarehouseLoading, setIsWarehouseLoading] = useState(false);
  const [hasLoadedWarehouses, setHasLoadedWarehouses] = useState(false);
  const { setLoading } = useLoading();
  const userAuthority = localStorage.getItem("authority");
  const isInventoryManager = userAuthority === "ROLE_INVENTORY_MANAGER";

  const keyToUrlMap: Record<string, string> = {
    DASHBOARD: "/home",
    PURCHASE_ORDER: "/purchase-order",
    UID_PART_CODE_MAPPING: "/uid-part-code-mapping",
    DELIVERY_ORDER: "/delivery-order",
    APPROVAL_REQUEST: "/approval-list",
    TRANSFER_ORDER: "/transfer-order",
    SALES_ORDER: "/sales-order",
    PARTS: "/product-template",
    WAREHOUSE: "/warehouse",
    WAREHOUSE_MAPPING: "/warehouse-mapping",
    WAREHOUSE_BINS: "/warehouse-bins",
    PART_BIN_MAPPING: "/part-bin-mapping",
    VENDOR: "/vendor",
    RETURN_ORDER: "/return-order",
    DELIVERY_ORDER_RETURN: "/delivery-return",
    TRANSFER_ORDER_RETURN: "/transfer-return",
    INVENTORY_LIST: "/inventory-list",
    INVENTORY_HISTORY_REPORT: "/inventory-history-report",
    CREATE_PRODUCT_REPORT: "/create-product/report",
    PO_REPORT: "/po-report",
    DO_REPORT: "/do-report",
    BACK_ORDER_REPORT: "/back-order-report",
    SO_REPORT: "/so-report",
    TO_REPORT: "/to-report",
    SPARE_PART_FORECAST: "/spare-forecast",
    CREATE_USER: "/user-management/create",
    USERS: "/user-management/edit",
    WARRANTY_REQUESTS: "/warranty-requests",
    ACTION_HISTORY: "/action-history",
  };

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const response: GetModulesApiResponse =
        await GetModulesApi.api.getModule();
      setMenuItems(response.data.userModuleDtoList);
    } catch (error) {
      console.error("Error fetching menu items", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMenuItems();
  }, []);

  useEffect(() => {
    if (isInventoryManager) {
      void fetchWarehouses();
    }
  }, [isInventoryManager]);

  const handleMenuItemClick = (key: string) => {
    const route = keyToUrlMap[key];
    if (route) {
      navigateTo(route);
    }
  };

  const handleToggleSubMenu = (key: string) => {
    setExpandedMenuItems((prev) => {
      const nextExpanded = !prev[key];

      if (key === "WAREHOUSE_LIST" && nextExpanded && !hasLoadedWarehouses) {
        void fetchWarehouses();
      }

      return {
        ...prev,
        [key]: nextExpanded,
      };
    });
  };

  const fetchWarehouses = async () => {
    try {
      setIsWarehouseLoading(true);
      const response = await UserWarehouseApi.api.getAssignedWarehouses();
      const warehouseList = response?.data?.userWarehouseMappingList || [];
      if (Array.isArray(warehouseList)) {
        setWarehouses(warehouseList);
        setHasLoadedWarehouses(true);
      }
    } catch (error) {
      console.error("Error fetching warehouses", error);
      setWarehouses([]);
    } finally {
      setIsWarehouseLoading(false);
    }
  };

  const handleWarehouseSelect = async (warehouseId: number) => {
    try {
      setLoading(true);
      const response = await UserWarehouseApi.api.setDefaultWarehouse(warehouseId);
      console.debug("Warehouse selected successfully:", response);
      navigateTo("/home");
      window.location.reload();
    } catch (error) {
      console.error("Error selecting warehouse", error);
    } finally {
      setLoading(false);
    }
  };

  const defaultWarehouse = warehouses.find((warehouse) => warehouse.isDefault);
  const warehouseDropdownLabel = defaultWarehouse?.warehouseName || "Warehouse";

  const isActiveRoute = (key: string) => {
    const route = keyToUrlMap[key];

    if (!route) {
      return false;
    }

    const currentPath = location.pathname;
    return currentPath === route || currentPath.startsWith(`${route}/`);
  };

  const getMenuStyles = (key: string, isSubModule = false) => {
    const isActive = isActiveRoute(key);
    return [
      "flex items-center gap-3 rounded-md px-4 py-2 cursor-pointer transition-colors duration-200 text-sm font-medium w-full text-left",
      isSubModule ? "ml-6" : "mx-2",
      isActive
        ? "bg-gray-900 text-white shadow"
        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900",
    ].join(" ");
  };

  const rawDisplayMenuItems = menuItems;

  // Separate SPARE_PART_FORECAST from the rest — it always renders at the bottom
  const regularMenuItems = rawDisplayMenuItems.filter(
    (item) => item.key !== "SPARE_PART_FORECAST"
  );
  const spareForecastItem = rawDisplayMenuItems.find(
    (item) => item.key === "SPARE_PART_FORECAST"
  );

  const isSpareActive = isActiveRoute("SPARE_PART_FORECAST");

  return (
    <>
      <style>{`
        @keyframes orbit {
          from { transform: rotate(0deg) translateX(10px) rotate(0deg); }
          to   { transform: rotate(360deg) translateX(10px) rotate(-360deg); }
        }
        @keyframes orbit2 {
          from { transform: rotate(120deg) translateX(10px) rotate(-120deg); }
          to   { transform: rotate(480deg) translateX(10px) rotate(-480deg); }
        }
        @keyframes orbit3 {
          from { transform: rotate(240deg) translateX(10px) rotate(-240deg); }
          to   { transform: rotate(600deg) translateX(10px) rotate(-600deg); }
        }
        @keyframes pulseCore {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.7; transform: scale(0.85); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 8px 2px rgba(0,0,0,0.25), 0 0 20px 4px rgba(0,0,0,0.12); }
          50%       { box-shadow: 0 0 16px 4px rgba(0,0,0,0.4), 0 0 40px 8px rgba(0,0,0,0.2); }
        }
        @keyframes border-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .ai-btn-shimmer {
          background: linear-gradient(
            110deg,
            #0a0a0a 0%,
            #1a1a1a 30%,
            #3a3a3a 50%,
            #1a1a1a 70%,
            #0a0a0a 100%
          );
          background-size: 200% auto;
          animation: shimmer 3s linear infinite;
        }
        .ai-btn-shimmer-active {
          background: linear-gradient(
            110deg,
            #111111 0%,
            #222222 30%,
            #555555 50%,
            #222222 70%,
            #111111 100%
          );
          background-size: 200% auto;
          animation: shimmer 2s linear infinite;
        }
        .orbit-dot {
          position: absolute;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #d1d5db;
          top: 50%;
          left: 50%;
          margin-top: -2.5px;
          margin-left: -2.5px;
        }
        .orbit-dot-1 { animation: orbit  2.4s linear infinite; }
        .orbit-dot-2 { animation: orbit2 2.4s linear infinite; background: #ffffff; }
        .orbit-dot-3 { animation: orbit3 2.4s linear infinite; background: #9ca3af; }
        .ai-core {
          animation: pulseCore 2s ease-in-out infinite;
        }
        .ai-glow-wrap {
          animation: glow-pulse 2.5s ease-in-out infinite;
          border-radius: 12px;
        }
        .ai-label-text {
          background: linear-gradient(90deg, #d1d5db, #ffffff, #d1d5db);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 2.5s linear infinite;
        }
        .new-badge {
          background: linear-gradient(135deg, #f59e0b, #ef4444);
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.05em;
          border-radius: 4px;
          padding: 1px 4px;
          color: white;
          text-transform: uppercase;
          box-shadow: 0 1px 4px rgba(239,68,68,0.5);
        }
      `}</style>

      <aside
        className="fixed top-0 left-0 bottom-0 w-64 bg-white shadow-xl z-50 flex flex-col overflow-hidden"
        aria-label="Sidebar Navigation">
        {/* Header */}
        <div className="flex flex-row items-center gap-3 p-4 border-b border-gray-200 shrink-0">
          <img
            src="/assets/zypp-logo.png"
            alt="Zypp Logo"
            className="w-8 h-8 object-contain"
          />
          <h2 className="text-lg font-semibold text-gray-800">Zypp Inventory</h2>
        </div>

        {/* Navigation — scrollable */}
        <nav className="py-4 flex-1 overflow-y-auto">
          {isInventoryManager && (
            <div className="px-3 pb-3">
              <button
                type="button"
                className="flex w-[220px] items-center justify-between rounded-[6px] border border-[#D9E1EC] bg-[#F9F9F9] px-3 py-2 text-left text-sm font-semibold text-[#374151] shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors duration-200 hover:border-[#B7C2D3] hover:bg-[#F9F9F9]"
                onClick={() => handleToggleSubMenu("WAREHOUSE_LIST")}
                aria-expanded={expandedMenuItems.WAREHOUSE_LIST || false}
              >
                <span
                  className="w-4 h-4 rounded flex items-center justify-center text-[10px] font-semibold shrink-0 text-[#8B95A7]"
                >
                  W
                </span>
                <span className="flex-1 truncate px-2 text-left text-[14px] font-normal text-[#4B5563]">
                  {warehouseDropdownLabel}
                </span>
                <svg
                  className={`h-4 w-4 shrink-0 text-[#9CA3AF] transition-transform duration-200 ${expandedMenuItems.WAREHOUSE_LIST ? "rotate-180" : "rotate-0"
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {expandedMenuItems.WAREHOUSE_LIST && (
                <ul className="ml-2 mt-1 space-y-1">
                  {isWarehouseLoading ? (
                    <li className="px-4 py-2 text-xs text-gray-500 ml-6">
                      Loading warehouses...
                    </li>
                  ) : warehouses.length > 0 ? (
                    warehouses.map((warehouse) => (
                      <li key={warehouse.warehouseId}>
                        <button
                          className={getMenuStyles(`warehouse-${warehouse.warehouseId}`, true)}
                          onClick={() => handleWarehouseSelect(warehouse.warehouseId)}
                        >
                          <span className="w-4 h-4 rounded flex items-center justify-center text-xs shrink-0 bg-gray-200 text-gray-600">
                            {warehouse.warehouseCode.charAt(0)}
                          </span>
                          <span className="flex-1">
                            {warehouse.warehouseName}
                          </span>
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="px-4 py-2 text-xs text-gray-500 ml-6">
                      No warehouses assigned
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          <ul className="space-y-1">
            {regularMenuItems.map((menuItem) => (
              <li key={menuItem.key}>
                <button
                  className={getMenuStyles(menuItem.key)}
                  onClick={() => {
                    if (menuItem.subModuleList.length > 0) {
                      handleToggleSubMenu(menuItem.key);
                    } else {
                      handleMenuItemClick(menuItem.key);
                    }
                  }}
                  aria-expanded={
                    menuItem.subModuleList.length > 0
                      ? expandedMenuItems[menuItem.key] || false
                      : undefined
                  }>
                  <span
                    className={`w-5 h-5 rounded flex items-center justify-center text-xs shrink-0 ${isActiveRoute(menuItem.key)
                        ? "bg-white text-gray-900"
                        : "bg-gray-200 text-gray-600"
                      }`}>
                    {menuItem.key.charAt(0)}
                  </span>
                  <span className="flex-1">
                    {menuItem.value}
                  </span>
                  {menuItem.subModuleList.length > 0 && (
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${expandedMenuItems[menuItem.key] ? "rotate-180" : "rotate-0"
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  )}
                </button>

                {menuItem.subModuleList.length > 0 &&
                  expandedMenuItems[menuItem.key] && (
                    <ul className="ml-2 mt-1 space-y-1">
                      {menuItem.subModuleList.map((subModule) => (
                        <li key={subModule.key}>
                          <button
                            className={getMenuStyles(subModule.key, true)}
                            onClick={() => handleMenuItemClick(subModule.key)}>
                            <span
                              className={`w-4 h-4 rounded flex items-center justify-center text-xs shrink-0 ${isActiveRoute(subModule.key)
                                  ? "bg-white text-gray-900"
                                  : "bg-gray-200 text-gray-600"
                                }`}>
                              {subModule.key.charAt(0)}
                            </span>
                            <span className="flex-1">
                              {subModule.value}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
              </li>
            ))}
          </ul>
        </nav>

        {/* ── AI Spare Part Forecast — always pinned at bottom ── */}
        {spareForecastItem && (
          <div className="shrink-0 px-3 pb-4 pt-2 border-t border-gray-100">
            {/* Divider label */}
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-2 px-1">
              AI-Powered
            </p>

            <div className={`ai-glow-wrap ${isSpareActive ? "opacity-100" : "opacity-90 hover:opacity-100"} transition-opacity duration-300`}>
              <button
                onClick={() => handleMenuItemClick("SPARE_PART_FORECAST")}
                className={`relative w-full flex items-center gap-3 px-4 py-3 rounded-xl overflow-hidden cursor-pointer border-0 transition-all duration-300 ${isSpareActive ? "ai-btn-shimmer-active" : "ai-btn-shimmer"
                  }`}
                aria-label="Spare Part Forecast — AI Powered">

                {/* Animated AI Orb Icon */}
                <div className="relative w-8 h-8 shrink-0 flex items-center justify-center">
                  {/* Orbiting dots */}
                  <span className="orbit-dot orbit-dot-1" />
                  <span className="orbit-dot orbit-dot-2" />
                  <span className="orbit-dot orbit-dot-3" />
                  {/* Core */}
                  <span className="ai-core relative z-10 w-4 h-4 rounded-full bg-gradient-to-br from-gray-200 to-white flex items-center justify-center shadow-lg">
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="black"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M12 2a4 4 0 0 1 4 4c0 1.5-.8 2.8-2 3.5V12h-4V9.5A4 4 0 0 1 8 6a4 4 0 0 1 4-4z" />
                      <path d="M8 12H5a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2h-3" />
                      <line x1="9" y1="21" x2="9" y2="18" />
                      <line x1="15" y1="21" x2="15" y2="18" />
                    </svg>
                  </span>
                </div>

                {/* Text */}
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="ai-label-text text-sm font-bold leading-tight">
                      {spareForecastItem.value}
                    </span>
                    <span className="new-badge shrink-0">AI</span>
                  </div>
                  <span className="text-gray-400 text-[10px] font-medium leading-tight truncate block">
                    Predictive Intelligence
                  </span>
                </div>

                {/* Arrow */}
                <svg
                  className="w-4 h-4 text-gray-400 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default SideBarNavigation;